import type {
  ClothingSize,
  ClothingType,
  Collaborator,
  CollaboratorClothing,
  CollaboratorEquipment,
  CollaboratorRole,
  CollaboratorStatus,
  CollaboratorUnavailability,
  CollaboratorWorkTime,
  ContractType,
  EquipmentType,
  UnavailabityReason,
  WeekDay,
  WorkShift,
  WorkShiftClock,
} from '@gozoki/api-types'
import { gozokiApiWithAuth } from '../gozokiApi'

// ============================================================================
// Collaborators mutations
// ============================================================================

export interface CreateCollaboratorData {
  matricule: string
  firstName: string
  lastName: string
  email: string
  role: CollaboratorRole
  contractType: ContractType
  status: CollaboratorStatus
  phone: string
  managerId?: number | null
  monthlyHours: number
  hourlyCost: number
  storeIds: number[]
}

export type UpdateCollaboratorData = Partial<CreateCollaboratorData>

// Create a collaborator
export const createCollaborator = async (data: CreateCollaboratorData) => {
  const response = await gozokiApiWithAuth.post<Collaborator>('/hr/collaborators', data)
  return response.data
}

// Update a collaborator
export const updateCollaborator = async ({
  id,
  data,
}: {
  id: number
  data: UpdateCollaboratorData
}) => {
  const response = await gozokiApiWithAuth.patch<Collaborator>(`/hr/collaborators/${id}`, data)
  return response.data
}

// Delete a collaborator
export const deleteCollaborator = async (id: number) => {
  const response = await gozokiApiWithAuth.delete(`/hr/collaborators/${id}`)
  return response
}

// ============================================================================
// Collaborator work times mutations (batch operations)
// ============================================================================

export interface CreateCollaboratorWorkTimeData {
  dayOfWeek: WeekDay
  startTime: string // Format: "HH:mm"
  endTime: string // Format: "HH:mm"
}

// Replace-all work times for a collaborator.
// We use PUT to make the replace semantics explicit and avoid partial updates.
export const replaceCollaboratorWorkTimes = async ({
  collaboratorId,
  data,
}: {
  collaboratorId: number
  data: CreateCollaboratorWorkTimeData[]
}) => {
  const response = await gozokiApiWithAuth.put<CollaboratorWorkTime[]>(
    `/hr/collaborators/${collaboratorId}/work-times`,
    { workTimes: data },
  )
  return response.data
}

// ============================================================================
// Collaborator unavailabilities mutations
// ============================================================================

export interface CreateCollaboratorUnavailabilityData {
  startDate: string // ISO date string
  endDate: string // ISO date string
  startTime?: string | null // Format: "HH:mm"
  endTime?: string | null // Format: "HH:mm"
  reason: UnavailabityReason
}

export type UpdateCollaboratorUnavailabilityData = Partial<CreateCollaboratorUnavailabilityData>

// Create an unavailability for a collaborator
export const createCollaboratorUnavailability = async ({
  collaboratorId,
  data,
}: {
  collaboratorId: number
  data: CreateCollaboratorUnavailabilityData
}) => {
  const response = await gozokiApiWithAuth.post<CollaboratorUnavailability>(
    `/hr/collaborators/${collaboratorId}/unavailabilities`,
    data,
  )
  return response.data
}

// Update an unavailability for a collaborator
export const updateCollaboratorUnavailability = async ({
  collaboratorId,
  unavailabilityId,
  data,
}: {
  collaboratorId: number
  unavailabilityId: number
  data: UpdateCollaboratorUnavailabilityData
}) => {
  const response = await gozokiApiWithAuth.patch<CollaboratorUnavailability>(
    `/hr/collaborators/${collaboratorId}/unavailabilities/${unavailabilityId}`,
    data,
  )
  return response.data
}

// Delete an unavailability for a collaborator
export const deleteCollaboratorUnavailability = async ({
  collaboratorId,
  unavailabilityId,
}: {
  collaboratorId: number
  unavailabilityId: number
}) => {
  const response = await gozokiApiWithAuth.delete(
    `/hr/collaborators/${collaboratorId}/unavailabilities/${unavailabilityId}`,
  )
  return response
}

export interface ExportCollaboratorUnavailabilitiesToXlsxParams {
  storeId?: number
}

export const exportCollaboratorUnavailabilitiesToXlsx = async ({
  storeId,
}: ExportCollaboratorUnavailabilitiesToXlsxParams = {}) => {
  const response = await gozokiApiWithAuth.get<Blob>(
    '/hr/collaborators/unavailabilities/export-xlsx',
    {
      responseType: 'blob',
      params: { storeId },
    },
  )
  return response.data
}

// ============================================================================
// Collaborator equipment mutations
// ============================================================================

export interface CreateCollaboratorEquipmentData {
  type: EquipmentType
  givenDate: string // ISO date string
  serialNumber: string
  brand: string
}

export type UpdateCollaboratorEquipmentData = Partial<CreateCollaboratorEquipmentData>

// Create equipment for a collaborator
export const createCollaboratorEquipment = async ({
  collaboratorId,
  data,
}: {
  collaboratorId: number
  data: CreateCollaboratorEquipmentData
}) => {
  const response = await gozokiApiWithAuth.post<CollaboratorEquipment>(
    `/hr/collaborators/${collaboratorId}/equipment`,
    data,
  )
  return response.data
}

// Update equipment for a collaborator
export const updateCollaboratorEquipment = async ({
  collaboratorId,
  equipmentId,
  data,
}: {
  collaboratorId: number
  equipmentId: number
  data: UpdateCollaboratorEquipmentData
}) => {
  const response = await gozokiApiWithAuth.patch<CollaboratorEquipment>(
    `/hr/collaborators/${collaboratorId}/equipment/${equipmentId}`,
    data,
  )
  return response.data
}

// Delete equipment for a collaborator
export const deleteCollaboratorEquipment = async ({
  collaboratorId,
  equipmentId,
}: {
  collaboratorId: number
  equipmentId: number
}) => {
  const response = await gozokiApiWithAuth.delete(
    `/hr/collaborators/${collaboratorId}/equipment/${equipmentId}`,
  )
  return response
}

// ============================================================================
// Collaborator clothing mutations
// ============================================================================

export interface CreateCollaboratorClothingData {
  type: ClothingType
  size: ClothingSize
  quantity: number
}

export type UpdateCollaboratorClothingData = Partial<CreateCollaboratorClothingData>

// Create clothing for a collaborator
export const createCollaboratorClothing = async ({
  collaboratorId,
  data,
}: {
  collaboratorId: number
  data: CreateCollaboratorClothingData
}) => {
  const response = await gozokiApiWithAuth.post<CollaboratorClothing>(
    `/hr/collaborators/${collaboratorId}/clothing`,
    data,
  )
  return response.data
}

// Update clothing for a collaborator
export const updateCollaboratorClothing = async ({
  collaboratorId,
  clothingId,
  data,
}: {
  collaboratorId: number
  clothingId: number
  data: UpdateCollaboratorClothingData
}) => {
  const response = await gozokiApiWithAuth.patch<CollaboratorClothing>(
    `/hr/collaborators/${collaboratorId}/clothing/${clothingId}`,
    data,
  )
  return response.data
}

// Delete clothing for a collaborator
export const deleteCollaboratorClothing = async ({
  collaboratorId,
  clothingId,
}: {
  collaboratorId: number
  clothingId: number
}) => {
  const response = await gozokiApiWithAuth.delete(
    `/hr/collaborators/${collaboratorId}/clothing/${clothingId}`,
  )
  return response
}

// ============================================================================
// Work shifts mutations
// ============================================================================

export interface CreateWorkShiftData {
  collaboratorId: number
  storeId: number
  date: string // ISO date string
  startTime: string // Format: "HH:mm"
  endTime: string // Format: "HH:mm"
}

export type UpdateWorkShiftData = Partial<CreateWorkShiftData>

// Create a work shift
export const createWorkShift = async (data: CreateWorkShiftData) => {
  const response = await gozokiApiWithAuth.post<WorkShift>('/hr/work-shifts', data)
  return response.data
}

// Update a work shift
export const updateWorkShift = async ({ id, data }: { id: number; data: UpdateWorkShiftData }) => {
  const response = await gozokiApiWithAuth.patch<WorkShift>(`/hr/work-shifts/${id}`, data)
  return response.data
}

// Delete a work shift
export const deleteWorkShift = async (id: number) => {
  const response = await gozokiApiWithAuth.delete(`/hr/work-shifts/${id}`)
  return response
}

// ============================================================================
// Attendance mutations
// ============================================================================

export interface ClockInData {
  collaboratorId: number
  temoinFile: File | Blob
}

export interface ClockOutData {
  collaboratorId: number
  temoinFile: File | Blob
}

export interface ExportWorkShiftClocksToXlsxParams {
  storeId?: number
  dateStart: string
  dateEnd: string
}

// Clock in for a collaborator
export const clockIn = async (data: ClockInData) => {
  const formData = new FormData()
  formData.append('collaboratorId', data.collaboratorId.toString())
  formData.append('temoinFile', data.temoinFile)

  const response = await gozokiApiWithAuth.post<WorkShiftClock>(
    '/hr/attendances/clock-in',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data
}

// Clock out for a collaborator
export const clockOut = async (data: ClockOutData) => {
  const formData = new FormData()
  formData.append('collaboratorId', data.collaboratorId.toString())
  formData.append('temoinFile', data.temoinFile)

  const response = await gozokiApiWithAuth.post<WorkShiftClock>(
    '/hr/attendances/clock-out',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data
}

export const exportWorkShiftClocksToXlsx = async (data: ExportWorkShiftClocksToXlsxParams) => {
  const response = await gozokiApiWithAuth.get<Blob>('/hr/attendances/export-xlsx', {
    params: data,
    responseType: 'blob',
  })
  return response.data
}

// ============================================================================
// Planning mutations
// ============================================================================

export interface OptimizePlanningData {
  dateStart: string // ISO date string (YYYY-MM-DD)
  dateEnd: string // ISO date string (YYYY-MM-DD)
}

export interface OptimizePlanningResponse {
  status: string
  createdShifts: number
}

// Optimize planning
export const optimizePlanning = async (data: OptimizePlanningData) => {
  const response = await gozokiApiWithAuth.post<OptimizePlanningResponse>(
    '/hr/planning/optimize',
    data,
  )
  return response.data
}
